import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { Store } from 'components/common/types/Store.types';
import { PoiOption, DataProvidersWithPoiOptions } from 'components/common/types/Planner.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import searchOptions from 'utils/searchOptions';
import { fetchAndSetPoiList } from 'store/userOptions/actions';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { SearchOptions, UsePOIData } from './PointsOfInterest.types';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const usePOIData = (): UsePOIData => {
  const dispatch = useDispatch();
  const dataProvidersWithPoiOptions = useSelector((state: Store) => state.userOptions.dataProvidersWithPoiOptions);
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  useEffect(() => {
    if (isDirectSalesCampaignType && Object.keys(dataProvidersWithPoiOptions).length === 0) {
      dispatch(fetchAndSetPoiList(cancelFunctions));
    }
  }, []);

  const [searchResults, setSearchResults] = useState<DataProvidersWithPoiOptions>({});
  const [searchTerm, setSearchTerm] = useState('');

  const clearSearchResults = (): void => {
    setSearchResults({});
    setSearchTerm('');
  };

  const searchNestedPoiOptions = (poiOptions: PoiOption[], searchText: string): PoiOption[] => {
    return searchOptions(poiOptions, searchText);
  };

  const debouncedSearch = useCallback(
    debounce((options: SearchOptions) => {
      const { text, dataProvider } = options;

      if (!dataProvidersWithPoiOptions[dataProvider]) {
        setSearchResults({});
        return;
      }

      const dataProviderPoiOptions = dataProvidersWithPoiOptions[dataProvider];
      const results = searchNestedPoiOptions(dataProviderPoiOptions, text);

      setSearchResults({ [dataProvider]: results });
    }, 300),
    [dataProvidersWithPoiOptions],
  );

  const searchPoi = (options: SearchOptions): void => {
    setSearchTerm(options.text ?? '');
    debouncedSearch(options);
  };

  return {
    clearSearchResults,
    searchPoi,
    searchTerm,
    dataProvidersWithPoiOptions: searchTerm.length ? searchResults : dataProvidersWithPoiOptions,
  };
};
